<template>
  <Dialog
    :visible="visible"
    :aria-labelledby="titleId"
    :aria-modal="true"
    modal
    :style="{ width: width, height: height, margin: margin }"
    :class="[
      dialogClass,
      'relative rounded-[24px]',
      { 'fullscreen-dialog': isFullscreen },
      { 'has-custom-margin': margin },
    ]"
    :closable="closable"
    :header="title"
    @update:visible="emit('update:visible', $event)"
    v-bind="$attrs"
    :draggable="draggable"
  >
    <template #header>
      <template v-if="$slots.header || title || showCloseIcon">
        <template v-if="$slots.header">
          <div class="w-full">
            <slot name="header"></slot>
            <div
              v-if="showHeaderDivider"
              class="h-px mx-4 mt-2 bg-whiteBlue"
            ></div>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col w-full">
            <div
              class="flex justify-between items-center w-full mb-2 laptop:mb-4 ultra:mb-6"
            >
              <div
                class="flex items-center gap-2"
                :class="{
                  'justify-center w-full': titleAlign === 'center',
                  'justify-start': titleAlign === 'left',
                  'justify-end': titleAlign === 'right',
                }"
              >
                <span
                  v-if="typeof title === 'string'"
                  :id="props.title ? 'base-dialog-title' : null"
                  class="text-xl font-semibold"
                >
                  {{ title }}
                </span>

                <span v-else v-html="title"></span>
              </div>
              <button
                v-if="showCloseIcon"
                @click="emit('update:visible', false)"
                class="cursor-pointer border-none bg-transparent"
                v-html="closeSvg({ width: '40px', height: '40px' })"
              ></button>
            </div>
            <div
              v-if="showHeaderDivider"
              class="w-full h-px bg-whiteBlue"
            ></div>
          </div>
        </template>
      </template>
    </template>

    <slot></slot>

    <template #footer>
      <slot name="footer"></slot>
    </template>
  </Dialog>
</template>

<script setup>
import { closeSvg } from "@/utils/commonSvg";
import { computed, ref, onBeforeUnmount, onMounted } from "vue";

const props = defineProps({
  visible: Boolean,
  width: {
    type: String,
    default: "400px",
  },
  height: {
    type: String,
    default: "auto",
  },
  closable: {
    type: Boolean,
    default: false,
  },
  dialogClass: {
    type: String,
    default: "",
  },
  title: {
    type: [String, Object],
    default: "",
  },
  titleAlign: {
    type: String,
    default: "left",
    validator: (v) => ["left", "center", "right"].includes(v),
  },
  titleClass: {
    type: String,
    default: "",
  },
  svgWidth: {
    type: String,
    default: "24px",
  },
  svgHeight: {
    type: String,
    default: "24px",
  },
  draggable: {
    type: Boolean,
    default: false,
  },
  defaultTitleStyle: {
    type: Boolean,
    default: false,
  },
  fullscreen: { type: Boolean, default: false },
  showCloseIcon: { type: Boolean, default: false },
  headerContent: {
    type: String,
    default: "",
  },
  showHeaderDivider: {
    type: Boolean,
    default: false,
  },
  margin: {
    type: String,
    default: "",
  },
});

const titleId = computed(() => {
  if (props.title) return "base-dialog-title";
  return null;
});

const emit = defineEmits(["update:visible"]);
const isSmallScreen = ref(window.innerWidth < 768);

const checkScreenSize = () => {
  isSmallScreen.value = window.innerWidth < 768;
};

const isFullscreen = computed(() => props.fullscreen && isSmallScreen.value);

onMounted(() => {
  if (props.fullscreen) {
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
  }
});

onBeforeUnmount(() => {
  if (props.fullscreen) {
    window.removeEventListener("resize", checkScreenSize);
  }
});

</script>

<style>
.p-dialog {
  padding: 0 !important;
  border: 0px !important;
}

.p-dialog:not(.has-custom-margin) {
  margin: 0 !important;
}

.p-dialog-header,
.p-dialog-content,
.p-dialog-footer {
  padding: 0 !important;
  margin: 0 !important;
  border: 0px !important;
}

.p-dialog-content {
  /* hide scrollbars */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  background: transparent !important;
}


.fullscreen-dialog {
  width: 100vw !important;
  height: 100dvh !important;
  max-width: 100vw !important;
  max-height: 100dvh !important;
  border-radius: 0 !important;
  display: flex !important;
  flex-direction: column !important;
}

.fullscreen-dialog .p-dialog-content {
  flex: 1 1 auto !important;
  overflow-y: auto !important;
  min-height: 0 !important;
}

.fullscreen-dialog .p-dialog-header,
.fullscreen-dialog .p-dialog-footer {
  flex: 0 0 auto !important;
}


/* Mobile-only: remove rounded corners from dialogs that opt-in via `mobile-dialog` */
@media (max-width: 767px) {
  /* Flatten only dialogs marked with mobile-dialog */
  .p-dialog.mobile-dialog {
    border-radius: 0 !important;
  }

  /* Many dialog wrappers apply classes like rounded-[24px] or rounded-[23.04px] */
  /* Target only these wrapper radii inside mobile-dialog, not circular icons (rounded-full) */
  .p-dialog.mobile-dialog .rounded-\[24px\],
  .p-dialog.mobile-dialog .rounded-\[23\.04px\],
  .p-dialog.mobile-dialog .rounded-2xl,
  .p-dialog.mobile-dialog .rounded-xl,
  .p-dialog.mobile-dialog .confirmation-dialog {
    border-radius: 0 !important;
  }
}
</style>
